import React, { useContext, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import idsWithoutImages from './idsWithoutImages';
import Share from 'components/Dashboard/components/Share';
import { net_api_url, store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import StakeholderHoverLink from 'components/Dashboard/components/StakeholderHoverLink';
import { useNavigate } from 'react-router-dom';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import time from 'utils/time';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import TextSelector from 'components/Common/TextSelector';
import isValidURL from 'components/Dashboard/utilities/isValidURL';
import getAppSite from 'utils/getAppSite';
import { useAuth0 } from '@auth0/auth0-react';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import { OrganisationLogo } from 'components/Dashboard/UserContent/ui/InfluenceListItem';
import RateUpdatesContentButton from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContentButton';

const ShareButtonRender = (props) => {
  const { currentNew, shareButtonWidget, embed } = props;
  const { id, subject, objectId, objectType, embargoed, canonicalUrl } = currentNew ?? {};
  let item = {
    id,
    title: subject,
    objectID: objectId,
    type: objectType,
    embargoed,
  };

  const ShareOptions = {
    item,
    id: item.id,
    title: item.subject,
    embargoed,
    shareButtonWidget,
    itemShare: embed,
  };

  if (canonicalUrl) {
    ShareOptions.link = `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}`;
  }

  return <Share {...ShareOptions} />;
};

const DateHeading = (props) => {
  const { className, currentNew, reportWidget, notIncludeBranding, shareButtonWidget, embed } = props;
  const { isAuthenticated } = useAuth0();
  return (
    <div className={`row mx-0 justify-content-between align-items-center keyupdate-contente-date pt-2 ${className}`}>
      <p className='heading-date mb-0'>
        <span className='date'>{time(currentNew.dateTimeAdded).formatLongDate(true)}</span>
        {getAppSite() === 'global' && (
          <>
            <span className='dot'></span>
            <span className='date'>{time(currentNew.dateTimeAdded).formatTime()}</span>
          </>
        )}
        {!!currentNew.source && isValidURL(currentNew?.source) ? (
          <>
            <span className='dot' />
            <a className='mb-0' href={currentNew?.source} target='_blank' rel='noopener noreferrer'>
              {reportWidget ? 'Source' : 'Go to source'}: {new URL(currentNew?.source)?.hostname}
            </a>
          </>
        ) : (
          <>
            {currentNew.sourceOrganisation && (
              <>
                <span className='dot' />
                <span className='date'>Source: {currentNew.sourceOrganisation}</span>
              </>
            )}
          </>
        )}
      </p>
      {!isAuthenticated && !notIncludeBranding && (
        <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
      )}
    </div>
  );
};

const KeyUpdateNewContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { search } = state;

  const { KeyUpdates } = contentResults;
  const { list } = search;
  const {
    currentNew,
    shareButtonWidget,
    handPickedNew,
    contentContainer,
    hideSummary,
    reportWidget,
    embed,
    scrollbarRef,
    scrolling,
    isFromPopup,
    notIncludeBranding,
    notShowShare,
  } = props;
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { isAuthenticated } = useAuth0();

  const { createMarkup } = useCreateMarkup();

  const highlightedConditional = () => {
    return list !== null || list === null || (shareButtonWidget && !handPickedNew);
  };

  const keyupdateContainerRef = useRef(null);

  useEffect(() => {
    const container = keyupdateContainerRef.current;

    const processStakeholders = () => {
      if (!container || isMobile || reportWidget) return;
      const anchors = container.querySelectorAll('a');
      anchors.forEach((item) => {
        if (!item.isConnected) return;
        if (!item.href || !isValidURL(item?.href)) return;
        if (item.closest('.stakeholder-hover-container')) return;
        const urlItem = new URL(item.href);
        const pathname = urlItem.pathname;
        if (
          pathname.startsWith('/stakeholders') ||
          pathname.startsWith('/monitor/stakeholders') ||
          pathname.startsWith('/people/stakeholders') ||
          pathname.startsWith('/stakeholder-mapping')
        ) {
          const wrapper = document.createElement('span');
          wrapper.className = 'stakeholder-hover-container';
          wrapper.style.display = 'inline';
          wrapper.textContent = item.textContent;
          item.replaceWith(wrapper);
          const el = createRoot(wrapper);
          el.render(
            <StakeholderHoverLink
              contactId={pathname.match(/[0-9]+/)[0]}
              name={wrapper.textContent}
              contentContainer={contentContainer}
              navigate={navigate}
            />
          );
        }
      });
    };

    processStakeholders();

    if (container && getAppSite() === 'usa') {
      const pTags = container.querySelectorAll('p[style]');
      pTags.forEach((tag) => tag.removeAttribute('style'));
      const blockElements = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE'];
      const brTags = container.querySelectorAll('br');
      brTags.forEach((tag) => {
        if (tag.closest('p')) return;
        let next = tag.nextSibling;
        while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && !next.textContent.trim()))) {
          next = next.nextSibling;
        }
        if (next && blockElements.includes(next.nodeName)) {
          tag.remove();
        }
      });
    }
  }, [isMobile, reportWidget, currentNew?.id]);

  const titleSection = useRef(null);

  const HeadingTitle = () => {
    return (
      <h3
        className='main-title news-title pr-lg-4'
        dangerouslySetInnerHTML={createMarkup(
          highlightedConditional() && !!currentNew.subjectHighlighted
            ? currentNew.subjectHighlighted
            : currentNew.subject
        )}
      />
    );
  };

  let { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection });
  const index = KeyUpdates ? KeyUpdates?.hits?.findIndex((item) => item.id === currentNew.id) : null;

  const imageCondition = !shareButtonWidget && isAuthenticated;
  const mainContentContainerRef = useRef(null);

  const createImageUrl = () => {
    let source = currentNew?.source;
    if (source && isValidURL(source)) {
      const url = new URL(source);
      source = `https://img.logo.dev/${url.hostname}`;
    }
    return source;
  };

  return (
    <div ref={mainContentContainerRef}>
      <div
        className={`section-title pb-2 keyupdate-content-title ${
          reportWidget ? 'content-title-in-widget' : 'content-title-with-summary'
        } content-border-title`}
        ref={titleSection}
      >
        <div className='d-flex align-items-lg-center justify-content-between'>
          <div className='pr-lg-5 pr-3 content-new-title flex-grow-1'>
            <HeadingTitle />
            {!isMobile && (
              <DateHeading
                className={'d-none d-sm-flex flex-wrap'}
                currentNew={currentNew}
                reportWidget={reportWidget}
                notIncludeBranding={notIncludeBranding}
                shareButtonWidget={shareButtonWidget}
                embed={embed}
              />
            )}
          </div>
          {imageCondition && (
            <>
              {getAppSite() === 'usa' ? (
                <div className='d-none d-sm-block'>
                  <OrganisationLogo notShowDefaultImage={true} imageUrl={createImageUrl()} index={index} />
                </div>
              ) : (
                <div
                  className='content-image content-new-image d-none d-sm-block'
                  style={{
                    backgroundImage: currentNew.imageUrl
                      ? `url(${currentNew.imageUrl})`
                      : currentNew.imageId !== 0 && !idsWithoutImages.includes(currentNew.imageId)
                        ? `url('https://www.parliamenttoday.com/image.php?imageid=${currentNew.imageId}&mode=1')`
                        : `url('${import.meta.env.VITE_APP_CDNURL}/images/keyupdates-placeholder.png')`,
                  }}
                />
              )}
            </>
          )}
        </div>
        {isMobile && (
          <DateHeading className={'d-flex flex-wrap d-sm-none'} currentNew={currentNew} reportWidget={reportWidget} />
        )}
        <div className='flex-centered justify-content-between my-2 '>
          <div className='flex-centered'>
            {currentNew.embargoed && (
              <p className='hand-picked-item under-embargo-item'>
                <span>
                  Embargoed{' '}
                  {!!currentNew.embargoedUntil ? `until ${time(currentNew.embargoedUntil).formatEmbargoedDate()}` : ''}
                </span>
              </p>
            )}
            <RateUpdatesContentButton currentNew={currentNew} />
          </div>

          {!notShowShare && isAuthenticated && (
            <div className='flex-centered'>
              {!notIncludeBranding && (
                <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
              )}
            </div>
          )}
        </div>
      </div>
      {!shareButtonWidget && <ContentSummary content={currentNew} hide={hideSummary} />}
      <div className='pb-3 pb-lg-4 px-3 px-lg-4 specific-content-container mt-2'>
        {/* FIXED TITLE */}
        <div
          className={`content-item-scrolled-title section-title bg-white ${
            showFixedTitle && !isFromPopup && !reportWidget ? 'item-scrolled-title-visible' : ''
          }`}
          style={titleStyles}
        >
          <div className='d-flex justify-content-between scrolled-title-container'>
            <HeadingTitle />
            {!notShowShare && (
              <>
                {!notIncludeBranding && (
                  <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} />
                )}
              </>
            )}
          </div>
        </div>
        {/* END FIXED TITLE */}
        <TextSelector
          currentNew={currentNew}
          scrollbarRef={scrollbarRef}
          scrolling={scrolling}
          reportWidget={reportWidget}
        >
          <div
            className={`news-content-in pt-2 pb-3 pr-3 context mb-2 ${
              getAppSite() === 'usa' ? 'news-us-content' : ''
            } key-update-content-container`}
            ref={keyupdateContainerRef}
            dangerouslySetInnerHTML={createMarkup(currentNew.body?.replaceAll('\n', '\n\n'))}
          />
          {!!currentNew?.attachments?.length > 0 && !currentNew?.extract && (
            <div className='section-title py-3 mb-3 key-update-attachments'>
              <h3 className='general-complementary-title mb-3'>Attachments</h3>
              <ul className='policy-list px-0'>
                {currentNew.attachments.map((item) => {
                  const { filename, id } = item;
                  return <AttachmentLink key={`item-${id}`} filename={filename} id={id} />;
                })}
              </ul>
            </div>
          )}
        </TextSelector>
      </div>
    </div>
  );
};

const AttachmentLink = (props) => {
  const [fetching, setFetching] = useState(false);
  const { id, filename } = props;
  return (
    <li
      data-tooltip-content={'Download'}
      data-tooltip-id={'general-tooltip'}
      data-tooltip-float={true}
      key={`attachment-${id}`}
      className='attachment-box'
      onClick={(e) => {
        setFetching(true);
        fetch(`${net_api_url}/api/key-update/attachment/${id}`)
          .then((response) => response.blob())
          .then((blob) => {
            setFetching(false);
            const blobURL = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobURL;
            a.style = 'display: none';

            if (filename && filename.length) a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          })
          .catch(() => {});
      }}
    >
      <span className='attachment-file-name'>{filename}</span>
      <button disabled={fetching} className={`general-button`}>
        {fetching ? (
          <i className='fas fa-spinner fa-spin mx-3'></i>
        ) : (
          <span className='icon-download title-h5-m mx-3' />
        )}
      </button>
    </li>
  );
};

export { AttachmentLink };
export default KeyUpdateNewContent;
